import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Bell, Car, Clock, AlertTriangle, CheckCircle2, Calendar, Gauge } from 'lucide-react';
import { formatDistance } from '../utils/measurements';
import { getBackendUrl, getApiUrl } from '../utils/config';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

const ServiceReminders = ({ onSelectCar, userPreferences }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServiceReminders();
  }, []);

  const fetchServiceReminders = async () => {
    try {
      const response = await axios.get(`${API}/service-reminders`);
      setReminders(response.data);
    } catch (error) {
      console.error('Error fetching service reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'overdue': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'due_soon': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    }
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'overdue': return <Badge className="bg-red-100 text-red-800 border-red-200">Overdue</Badge>;
      case 'due_soon': return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Due Soon</Badge>;
      default: return <Badge className="bg-green-100 text-green-800 border-green-200">Upcoming</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatTaskType = (taskType) => {
    return taskType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="w-5 h-5 mr-2 text-blue-600" />
            Service Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-gray-500">Loading reminders...</div>
        </CardContent>
      </Card>
    );
  }

  if (reminders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="w-5 h-5 mr-2 text-blue-600" />
            Service Reminders
          </CardTitle>
          <CardDescription>
            Stay on top of your vehicle maintenance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              Great! You have no overdue or upcoming service reminders.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const overdueCount = reminders.filter(r => r.urgency === 'overdue').length;
  const dueSoonCount = reminders.filter(r => r.urgency === 'due_soon').length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Bell className="w-5 h-5 mr-2 text-blue-600" />
            Service Reminders
            {(overdueCount > 0 || dueSoonCount > 0) && (
              <Badge className="ml-2 bg-red-100 text-red-800">
                {overdueCount + dueSoonCount}
              </Badge>
            )}
          </div>
        </CardTitle>
        <CardDescription>
          {overdueCount > 0 && (
            <span className="text-red-600 font-medium">
              {overdueCount} overdue service(s)
            </span>
          )}
          {overdueCount > 0 && dueSoonCount > 0 && <span className="text-gray-500"> • </span>}
          {dueSoonCount > 0 && (
            <span className="text-yellow-600 font-medium">
              {dueSoonCount} due soon
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {reminders.slice(0, 5).map((reminder, index) => (
            <div 
              key={`${reminder.car_id}-${reminder.task_type}-${index}`}
              className={`p-4 border rounded-lg ${
                reminder.urgency === 'overdue' ? 'border-red-200 bg-red-50' :
                reminder.urgency === 'due_soon' ? 'border-yellow-200 bg-yellow-50' :
                'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-shrink-0 mt-1">
                    {getUrgencyIcon(reminder.urgency)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-gray-900">
                        {formatTaskType(reminder.task_type)}
                      </h4>
                      {getUrgencyBadge(reminder.urgency)}
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Car className="w-4 h-4 mr-1" />
                      <span>
                        {reminder.car_info.year} {reminder.car_info.make} {reminder.car_info.model}
                      </span>
                    </div>

                    {reminder.task_description && (
                      <p className="text-sm text-gray-700 mb-2">
                        {reminder.task_description}
                      </p>
                    )}

                    <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                      <div>
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>Last: {formatDate(reminder.last_service_date)}</span>
                        </div>
                        {reminder.next_due_date && (
                          <div className="flex items-center mt-1">
                            <Calendar className="w-3 h-3 mr-1" />
                            <span>Due: {formatDate(reminder.next_due_date)}</span>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <div className="flex items-center">
                          <Gauge className="w-3 h-3 mr-1" />
                          <span>Last: {formatDistance(reminder.last_service_odometer, userPreferences?.measurement_system)}</span>
                        </div>
                        {reminder.next_due_odometer && (
                          <div className="flex items-center mt-1">
                            <Gauge className="w-3 h-3 mr-1" />
                            <span>Due: {formatDistance(reminder.next_due_odometer, userPreferences?.measurement_system)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {(reminder.days_overdue || reminder.kilometers_overdue) && (
                      <div className="mt-2 text-xs font-medium">
                        {reminder.urgency === 'overdue' && (
                          <span className="text-red-600">
                            Overdue by: {' '}
                            {reminder.days_overdue && `${reminder.days_overdue} days`}
                            {reminder.days_overdue && reminder.kilometers_overdue && ' • '}
                            {reminder.kilometers_overdue && formatDistance(reminder.kilometers_overdue, userPreferences?.measurement_system)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSelectCar && onSelectCar(reminder.car_id)}
                  className="ml-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  View Car
                </Button>
              </div>
            </div>
          ))}

          {reminders.length > 5 && (
            <div className="text-center pt-2">
              <p className="text-sm text-gray-500">
                Showing 5 of {reminders.length} reminders
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceReminders;